import PageSEO from '@/components/PageSEO';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Phone, Home, Sparkles, X } from 'lucide-react';
import ProudlyServingSection from '@/components/ProudlyServingSection';

// Framed Mirror Collections Data
const framedMirrorCollections = [
  {
    series: 'Legacy',
    category: 'Traditional',
    frames: [
      { id: '418-2108', color: 'Silver', frameImage: '/images/mirrors/418-2108.jpg', profileImage: '/images/mirrors/418-2108-dim.webp' },
      { id: '417-2107', color: 'Gold', frameImage: '/images/mirrors/417-2107.jpg', profileImage: '/images/mirrors/417-2107-dim.webp' },
    ]
  },
  {
    series: 'Trevi',
    category: 'Traditional',
    frames: [
      { id: '442-7007', color: 'Gold/Dark', frameImage: '/images/mirrors/442-7007.jpg', profileImage: '/images/mirrors/442-7007-DIM.webp' },
      { id: '632-6007', color: 'Gold', frameImage: '/images/mirrors/632-6007.jpg', profileImage: '/images/mirrors/632-6007-DIM.webp' },
      { id: '632-6008', color: 'Silver', frameImage: '/images/mirrors/632-6008.jpg', profileImage: '/images/mirrors/632-6008-DIM.webp' },
    ]
  },
  {
    series: 'Positano',
    category: 'Traditional',
    frames: [
      { id: '451-1007', color: 'Gold', frameImage: '/images/mirrors/451-1007.jpg', profileImage: '/images/mirrors/451-1007-DIM.webp' },
      { id: '451-1010', color: 'Black', frameImage: '/images/mirrors/451-1010.jpg', profileImage: '/images/mirrors/451-1010-DIM.webp' },
      { id: '451-1008', color: 'Silver', frameImage: '/images/mirrors/451-1008.jpg', profileImage: '/images/mirrors/451-1008-DIM.webp' },
    ]
  },
  {
    series: 'Baltica',
    category: 'Traditional',
    frames: [
      { id: '240-7007', color: 'Gold', frameImage: '/images/mirrors/240-7007.jpg', profileImage: '/images/mirrors/240-7007-DIM.webp' },
      { id: '240-7008', color: 'Silver', frameImage: '/images/mirrors/240-7008.png', profileImage: '/images/mirrors/240-7008-DIM.webp' },
      { id: '240-7009', color: 'Pewter', frameImage: '/images/mirrors/240-7009.png', profileImage: '/images/mirrors/240-7009-DIM.webp' },
    ]
  },
  {
    series: 'Stella',
    category: 'Contemporary',
    frames: [
      { id: '141-7007', color: 'Gold', frameImage: '/images/mirrors/141-7007.jpg', profileImage: '/images/mirrors/141-7007-DIM.webp' },
      { id: '141-7008', color: 'Silver', frameImage: '/images/mirrors/141-7008.png', profileImage: '/images/mirrors/141-7008-DIM.webp' },
      { id: '141-7009', color: 'Pewter', frameImage: '/images/mirrors/141-7009.png', profileImage: '/images/mirrors/141-7009-DIM.webp' },
    ]
  },
  {
    series: 'Metal',
    category: 'Contemporary',
    frames: [
      { id: 'ML1003', color: 'Gold', frameImage: '/images/mirrors/ML1003.webp', profileImage: '' },
      { id: 'ML1101', color: 'Silver', frameImage: '/images/mirrors/ML1101.webp', profileImage: '' },
      { id: 'ML1102', color: 'Frosted Silver', frameImage: '/images/mirrors/ml1102.webp', profileImage: '' },
      { id: 'ML1121', color: 'Matte Black', frameImage: '/images/mirrors/ml1121.webp', profileImage: '' },
      { id: 'ML1123', color: 'Antique Gold', frameImage: '/images/mirrors/ml1123.webp', profileImage: '' },
    ]
  },
];

// Series Name Component with custom fonts
const SeriesName = ({ series, category }) => (
  <div className="flex items-baseline mb-6">
    <span 
      style={{ 
        fontFamily: "'Playfair Display', serif",
        color: '#b18c27',
        fontSize: '1.5rem',
        fontWeight: '500',
        position: 'relative',
        top: '-4px'
      }}
    >
      {series}
    </span>
    <span 
      style={{ 
        fontFamily: "'Snell Roundhand', cursive, 'Brush Script MT', cursive",
        color: '#000000',
        fontSize: '2rem',
        fontWeight: '400',
        fontStyle: 'italic'
      }}
    >
      {category}
    </span>
  </div>
);

// Frame Modal Component
const FrameModal = ({ frame, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('frame');
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <PageSEO title="Custom Mirrors" description="Custom-cut mirrors for bathrooms, bedrooms, and commercial spaces. Frameless, framed, beveled, backlit LED, and decorative options. Professional installation in the Hill Country." path="/mirrors" />
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-xl font-semibold">{frame.id} - {frame.color}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b">
          <button 
            className={`flex-1 py-3 text-center font-medium ${activeTab === 'frame' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
            onClick={() => setActiveTab('frame')}
          >
            Frame View
          </button>
          <button 
            className={`flex-1 py-3 text-center font-medium ${activeTab === 'profile' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
            onClick={() => setActiveTab('profile')}
          >
            Profile View
          </button>
        </div>
        
        {/* Image Content */}
        <div className="p-4">
          {activeTab === 'frame' ? (
            <div className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
              {frame.frameImage ? (
                <img src={frame.frameImage} alt={`${frame.id} Frame`} className="max-w-full max-h-full object-contain" />
              ) : (
                <span className="text-gray-400">Frame Image: {frame.id}</span>
              )}
            </div>
          ) : (
            <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
              {frame.profileImage ? (
                <img src={frame.profileImage} alt={`${frame.id} Profile`} className="max-w-full max-h-full object-contain" />
              ) : (
                <span className="text-gray-400">Profile Image: {frame.id}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const MirrorsPage = () => {
  const [selectedFrame, setSelectedFrame] = useState(null);

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Custom Mirror Installation",
    "name": "Custom Mirrors",
    "description": "Custom-cut mirrors for bathrooms, bedrooms, gyms, and decorative applications. Frameless, framed, beveled, and backlit LED options available.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Comal Glass Company",
      "telephone": "+1-830-359-9925"
    },
    "areaServed": { "@type": "State", "name": "Texas" }
  };
  
  const features = [
    'Custom sizes and shapes',
    'Frameless and framed options',
    'Beveled edge available',
    'Various mounting styles',
    'Anti-fog coating options',
    'Backlit LED integration',
    'Decorative and functional designs',
    'Professional installation included',
  ];

  const types = [
    {
      icon: Home,
      title: 'Bathroom Mirrors',
      description: 'Custom vanity mirrors with optional beveled edges and anti-fog coating.',
    },
    {
      icon: Sparkles,
      title: 'Decorative Mirrors',
      description: 'Statement pieces that enhance any room with style and elegance.',
    },
    {
      icon: Home,
      title: 'Full-Length Mirrors',
      description: 'Floor-to-ceiling mirrors perfect for bedrooms and dressing areas.',
    },
  ];

  return (
    <div className="min-h-screen">
      <PageSEO title="Custom Mirrors" description="Custom-cut mirrors for bathrooms, bedrooms, gyms, and decorative applications. Frameless, beveled, anti-fog, and backlit LED options. Professional installation in the Texas Hill Country." path="/mirrors" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/mirrors/mirror.jpg"
            alt="Custom mirrors"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        </div>

        <div className="container-custom relative z-10 text-white">
          <div className="max-w-3xl">
            <Badge className="mb-6 bg-primary/20 text-primary border-primary/30 backdrop-blur-sm">
              Custom Mirror Solutions
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Framed & Frameless <span className="text-gradient-cyan">Mirrors</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-8 leading-relaxed">
              Custom-cut mirrors for bathrooms, bedrooms, and any space. From elegant frameless designs to decorative framed pieces, we create mirrors that reflect your style.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover" asChild>
                <Link to="/contact">Get Free Quote</Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10" asChild>
                <a href="tel:8303599925">
                  <Phone className="mr-2 h-5 w-5" />
                  Call Now
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Types Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">Mirror Types</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Perfect for Every <span className="text-gradient-cyan">Application</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Custom mirrors tailored to your specific needs and style preferences.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {types.map((type, index) => {
              const Icon = type.icon;
              return (
                <Card key={index} className="border-none shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{type.title}</h3>
                    <p className="text-muted-foreground">{type.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-black text-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-primary/20 text-primary border-primary/30">Features & Options</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Custom <span className="text-gradient-cyan">Options</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-none shadow-md bg-white/10 backdrop-blur">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                    <p className="text-sm text-white/90">{feature}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Mirror Solutions Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4">Custom Solutions</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Specialty <span className="text-gradient-cyan">Mirror Applications</span>
              </h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Antique Mirrors</h3>
                    <p className="text-muted-foreground text-sm">
                      Elegant antiqued and vintage-style mirrors that add character and timeless sophistication to any space.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Gym & Fitness Mirrors</h3>
                    <p className="text-muted-foreground text-sm">
                      Large-format mirrors for commercial gyms, home fitness rooms, and dance studios with safety backing options.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Mirror Walls</h3>
                    <p className="text-muted-foreground text-sm">
                      Floor-to-ceiling mirror installations that visually expand spaces and enhance natural light.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Commercial Applications</h3>
                    <p className="text-muted-foreground text-sm">
                      Custom mirrors for retail stores, salons, restaurants, and hospitality venues.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="/images/mirrors/mirror_installation.jpg"
                alt="Custom mirror installation"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Framed Mirrors Collection Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">Premium Collections</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Framed <span className="text-gradient-cyan">Mirror Frames</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Explore our curated collection of elegant frame styles. Click on any frame to view detailed frame and profile images.
            </p>
          </div>

          {framedMirrorCollections.map((collection, collectionIndex) => (
            <div key={collectionIndex} className="mb-16 last:mb-0">
              <SeriesName series={collection.series} category={collection.category} />
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {collection.frames.map((frame, frameIndex) => (
                  <Card 
                    key={frameIndex} 
                    className="border shadow-md hover:shadow-xl transition-all cursor-pointer group overflow-hidden"
                    onClick={() => setSelectedFrame(frame)}
                  >
                    <div className="aspect-square bg-white relative overflow-hidden">
                      {frame.frameImage ? (
                        <img 
                          src={frame.frameImage} 
                          alt={`${frame.id} - ${frame.color}`} 
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300 bg-white"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300 text-4xl font-light">
                          {frame.id}
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                    </div>
                    <CardContent className="p-3 text-center">
                      <p className="font-medium text-sm">{frame.id}</p>
                      <p className="text-xs text-muted-foreground">{frame.color}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Frame Modal */}
      <FrameModal 
        frame={selectedFrame} 
        isOpen={!!selectedFrame} 
        onClose={() => setSelectedFrame(null)} 
      />

      {/* Gallery Preview */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Recent <span className="text-gradient-cyan">Mirror Projects</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[
              '/images/portfolio/Image-2.jpeg',
              '/images/portfolio/Image-7.jpeg',
              '/images/portfolio/Image-11.jpeg',
            ].map((image, index) => (
              <div key={index} className="aspect-square overflow-hidden rounded-lg shadow-lg">
                <img src={image} alt={`Mirror project ${index + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button variant="outline" size="lg" asChild>
              <Link to="/portfolio">View All Projects</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Proudly Serving Section */}
      <ProudlyServingSection serviceSlug="mirrors" />

      {/* CTA Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Create Your Perfect <span className="text-gradient-cyan">Mirror</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Custom-cut to your exact specifications with professional installation included.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover" asChild>
                <Link to="/contact">Get Free Quote</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="tel:8303599925">
                  <Phone className="mr-2 h-5 w-5" />
                  (830) 359-9925
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MirrorsPage;
